/* Product-grid cart-aware action.
   Server-confirmed (no optimistic UI): every Add / + / - shows a loader, writes
   to the cart, and only updates the visible quantity + header count AFTER the
   /cart.js response. State is read exclusively from /cart.js — never from
   localStorage / sessionStorage — so cached HTML can't show stale cart state. */
(function () {
  "use strict";

  var HINT_MS = 2200;
  // variantId(String) -> { quantity:Number, key:String }
  var cartMap = new Map();
  var cardState = new WeakMap();
  var hydrated = false;
  var hydratePromise = null;
  var cartItemCount = null;

  function readInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function variantIdOf(shell) {
    var input = shell.querySelector(".m_product_varaint_input");
    if (input && input.value) return String(input.value);
    return shell.dataset.variantId ? String(shell.dataset.variantId) : "";
  }

  function invCapOf(shell) {
    var raw = readInt(shell.dataset.variantQuantity, -1);
    return raw >= 0 ? raw : Infinity;
  }

  function stateFor(shell) {
    var state = cardState.get(shell);
    if (!state) {
      state = {
        serverQty: 0,
        busy: false,
        hintTimer: null,
        // Timestamp until which the Add button must ignore clicks. Set whenever
        // the stepper drops to 0 and the Add button is re-revealed in the same
        // spot, so the trailing half of a double-click on "-" (or a stray click
        // landing where the button just appeared) can't immediately re-add the
        // item. This is what stops the "decrease then it jumps back up" bug.
        addGuardUntil: 0
      };
      cardState.set(shell, state);
    }
    return state;
  }

  function currentCartCount() {
    if (Number.isFinite(cartItemCount)) return cartItemCount;
    var node = document.getElementById("cart_items_count");
    cartItemCount = node ? readInt((node.textContent || "").trim(), 0) : 0;
    return cartItemCount;
  }

  function setCartCount(count) {
    if (!Number.isFinite(count)) return;
    cartItemCount = Math.max(0, count);

    var nodes = document.querySelectorAll("#cart_items_count, .cart-count-h");
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = String(cartItemCount);
    }
  }

  function setMapEntry(variantId, quantity, key) {
    if (!variantId) return;
    var existing = cartMap.get(String(variantId));
    if (quantity > 0) {
      cartMap.set(String(variantId), {
        quantity: quantity,
        key: key || (existing && existing.key) || ""
      });
    } else {
      cartMap.delete(String(variantId));
    }
  }

  function entryFor(shell) {
    var variantId = variantIdOf(shell);
    return variantId ? cartMap.get(variantId) : null;
  }

  function showHint(shell, text) {
    var hint = shell.querySelector("[data-grid-hint]");
    if (!hint) return;

    var state = stateFor(shell);
    hint.textContent = text;
    hint.hidden = false;

    if (state.hintTimer) clearTimeout(state.hintTimer);
    state.hintTimer = setTimeout(function () {
      hint.hidden = true;
      hint.textContent = "";
    }, HINT_MS);
  }

  function showError(shell) {
    showHint(shell, "Couldn't update");
  }

  /* Toggle the per-card loading state. While busy, BOTH stepper buttons and the
     Add button are disabled and a spinner replaces the qty number, so a second
     click can't race the in-flight write. setBusy(false) always restores the
     buttons — this is what prevents the "both +/- freeze" lock. */
  function setBusy(shell, busy) {
    var state = stateFor(shell);
    state.busy = busy;

    var stepper = shell.querySelector("[data-grid-stepper]");
    var addBtn = shell.querySelector(".add-cart-btn");
    if (stepper) stepper.classList.toggle("is-loading", busy);
    if (busy) {
      if (addBtn) addBtn.classList.add("is-loading");
    } else if (addBtn) {
      addBtn.classList.remove("is-loading");
    }

    if (stepper) {
      var minus = stepper.querySelector("[data-qty-minus]");
      var plus = stepper.querySelector("[data-qty-plus]");
      if (minus) minus.disabled = busy;
      // Re-evaluate the inventory cap when unlocking so + only stays disabled
      // when genuinely at cap, never because a previous write left it disabled.
      if (plus) plus.disabled = busy || state.serverQty >= invCapOf(shell);
    }
  }

  /* Paint the confirmed quantity. Called only with server-confirmed values
     (on hydrate and after each cart write resolves). */
  function paint(shell, quantity) {
    var state = stateFor(shell);
    var qty = Math.max(0, quantity || 0);
    state.serverQty = qty;

    var addBtn = shell.querySelector(".add-cart-btn");
    var stepper = shell.querySelector("[data-grid-stepper]");
    if (!stepper) return;

    if (qty > 0) {
      var value = stepper.querySelector("[data-qty-value]");
      var plus = stepper.querySelector("[data-qty-plus]");

      if (value) value.textContent = String(qty);
      stepper.hidden = false;
      stepper.setAttribute("aria-hidden", "false");
      if (addBtn) addBtn.style.display = "none";
      if (plus) plus.disabled = state.busy || qty >= invCapOf(shell);
      var minus = stepper.querySelector("[data-qty-minus]");
      if (minus) minus.disabled = state.busy;
      return;
    }

    // Was the stepper visible before this paint hid it? If so, the Add button is
    // about to reappear under the user's pointer — arm the guard so a trailing
    // click can't instantly re-add. (No guard on the initial hydrate-to-0, where
    // the stepper was already hidden and there's no in-place button swap.)
    var wasShowingStepper = stepper.hidden === false;

    stepper.hidden = true;
    stepper.setAttribute("aria-hidden", "true");
    if (addBtn) {
      addBtn.style.display = "";
      addBtn.classList.remove("is-loading");
    }
    if (wasShowingStepper) state.addGuardUntil = Date.now() + 400;
  }

  function rebuildMap(cart) {
    cartMap.clear();
    if (!cart || !Array.isArray(cart.items)) return;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var variantId = String(item.variant_id || item.id || "");
      if (!variantId) continue;

      var existing = cartMap.get(variantId);
      if (existing) {
        existing.quantity += item.quantity || 0;
      } else {
        cartMap.set(variantId, {
          quantity: item.quantity || 0,
          key: item.key || ""
        });
      }
    }
  }

  function hydrateCard(shell) {
    var state = stateFor(shell);
    if (state.busy) return;

    var entry = entryFor(shell);
    var qty = entry ? entry.quantity : 0;
    paint(shell, qty);
  }

  function hydrateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var shells = scope.querySelectorAll("[data-grid-cart]");
    for (var i = 0; i < shells.length; i++) hydrateCard(shells[i]);
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    }).then(function (response) {
      if (!response.ok) throw new Error("Cart hydrate failed");
      return response.json();
    });
  }

  function hydrate() {
    if (hydratePromise) return hydratePromise;

    hydratePromise = fetchCart()
      .then(function (cart) {
        rebuildMap(cart);
        if (typeof cart.item_count === "number") setCartCount(cart.item_count);
        hydrated = true;
        hydrateAll(document);
        return cart;
      })
      .catch(function () {
        hydrated = true;
      });

    return hydratePromise;
  }

  function withDrawerSections(body) {
    var payload = {};
    body = body || {};
    for (var key in body) {
      if (Object.prototype.hasOwnProperty.call(body, key)) {
        payload[key] = body[key];
      }
    }
    payload.sections = ["cart-drawer", "cart-icon-bubble"];
    payload.sections_url = window.location.pathname;
    return payload;
  }

  function gridFetch(url, body) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-Grid-Cart-Stepper": "1"
      },
      body: JSON.stringify(withDrawerSections(body))
    }).then(function (response) {
      return response.text().then(function (text) {
        var json = null;
        try {
          json = text ? JSON.parse(text) : null;
        } catch (e) {
          json = null;
        }

        if (!response.ok || (json && json.status && json.status >= 400)) {
          var err = new Error((json && (json.description || json.message)) || "Cart request failed");
          err.payload = json;
          throw err;
        }

        return json;
      });
    });
  }

  function markDrawerDirty(cart, direction) {
    window.__gridStepperCartDirty = true;
    try {
      document.dispatchEvent(
        new CustomEvent("grid-stepper:cart-changed", {
          detail: { cart: cart || null, direction: direction || "none" }
        })
      );
    } catch (e) {}
  }

  /* Write an ABSOLUTE quantity for this card's variant and reconcile from the
     server response. No optimistic paint: the loader stays up until the cart
     confirms, then the real quantity + count are shown. */
  function writeQuantity(shell, nextQty) {
    var state = stateFor(shell);
    if (state.busy) return;

    var variantId = variantIdOf(shell);
    if (!variantId) return;

    var cap = invCapOf(shell);
    if (nextQty < 0) nextQty = 0;
    if (nextQty > cap) {
      nextQty = cap;
      if (Number.isFinite(cap)) showHint(shell, "Max " + cap);
    }
    if (nextQty === state.serverQty) return;

    var direction = nextQty > state.serverQty ? "up" : "down";

    setBusy(shell, true);

    var entry = cartMap.get(variantId);
    var id = (entry && entry.key) || variantId;

    gridFetch("/cart/change.js", { id: id, quantity: nextQty })
      .then(function (cart) {
        rebuildMap(cart);
        if (typeof cart.item_count === "number") setCartCount(cart.item_count);

        var fresh = cartMap.get(variantId);
        var serverQty = fresh ? fresh.quantity : 0;

        setBusy(shell, false);
        paint(shell, serverQty);
        if (serverQty < nextQty) showHint(shell, "Max " + serverQty);

        markDrawerDirty(cart, direction);
      })
      .catch(function () {
        setBusy(shell, false);
        paint(shell, state.serverQty);
        showError(shell);
      });
  }

  /* First add for a card not yet in the cart. Uses /cart/add.js so apps that key
     off the add event still fire, then confirms quantity from the response. */
  function handleAdd(shell) {
    var addBtn = shell.querySelector(".add-cart-btn");
    if (addBtn && addBtn.disabled) return;

    var state = stateFor(shell);
    if (state.busy) return;
    // Ignore a click that lands right after the stepper collapsed to 0 (the Add
    // button just reappeared in the same spot) — see addGuardUntil above.
    if (Date.now() < state.addGuardUntil) return;
    if (state.serverQty > 0) {
      // Already in cart (e.g. variant just re-hydrated) — step up instead.
      writeQuantity(shell, state.serverQty + 1);
      return;
    }

    var variantId = variantIdOf(shell);
    if (!variantId) return;

    var cap = invCapOf(shell);
    if (cap <= 0) {
      showHint(shell, "Max 0");
      return;
    }

    setBusy(shell, true);

    gridFetch("/cart/add.js", { id: variantId, quantity: 1 })
      .then(function (item) {
        var responseVariantId = String((item && (item.variant_id || item.id)) || variantId);
        var serverQty = item && typeof item.quantity === "number" ? item.quantity : 1;
        var key = (item && item.key) || (cartMap.get(responseVariantId) && cartMap.get(responseVariantId).key) || "";

        setMapEntry(responseVariantId, serverQty, key);
        setCartCount(currentCartCount() + serverQty);

        setBusy(shell, false);
        paint(shell, serverQty);

        markDrawerDirty(item, "up");
      })
      .catch(function () {
        setBusy(shell, false);
        paint(shell, state.serverQty);
        showError(shell);
      });
  }

  function handleStep(shell, delta) {
    var state = stateFor(shell);
    if (state.busy) return;
    writeQuantity(shell, state.serverQty + delta);
  }

  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var minus = target.closest("[data-qty-minus]");
    var plus = target.closest("[data-qty-plus]");
    if (!minus && !plus) return;

    var shell = target.closest("[data-grid-cart]");
    if (!shell) return;

    event.preventDefault();
    event.stopPropagation();

    var btn = minus || plus;
    if (btn.disabled) return;
    handleStep(shell, plus ? 1 : -1);
  });

  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var addBtn = target.closest(".add-cart-btn");
    if (!addBtn) return;

    var shell = addBtn.closest("[data-grid-cart]");
    if (!shell) return;

    event.preventDefault();
    handleAdd(shell);
  }, true);

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var shell = form.querySelector("[data-grid-cart]");
    if (!shell) return;

    event.preventDefault();
    handleAdd(shell);
  }, true);

  function resyncFromServer() {
    return fetchCart()
      .then(function (cart) {
        rebuildMap(cart);
        if (typeof cart.item_count === "number") setCartCount(cart.item_count);
        hydrateAll(document);
      })
      .catch(function () {});
  }

  var externalTimer = null;
  function scheduleExternalResync() {
    if (externalTimer) clearTimeout(externalTimer);
    externalTimer = setTimeout(resyncFromServer, 250);
  }

  document.addEventListener("cart:refresh", scheduleExternalResync);
  document.addEventListener("cart:updated", scheduleExternalResync);
  document.addEventListener("theme:cart:added", scheduleExternalResync);

  function repaintCardForSelect(select) {
    var card =
      select.closest(".m-product-card-wrapper") ||
      select.closest(".product-bx") ||
      select.closest(".prduct-filter") ||
      select.closest("form");
    if (!card) return;

    var shell = card.querySelector("[data-grid-cart]");
    if (shell) hydrateCard(shell);
  }

  document.addEventListener("change", function (event) {
    var select = event.target;
    if (!select || !select.classList) return;
    if (!select.classList.contains("m-sibling-select") && !select.classList.contains("m_variant_select")) return;

    setTimeout(function () {
      repaintCardForSelect(select);
    }, 0);
  });

  var observer = new MutationObserver(function (mutations) {
    if (!hydrated) return;
    for (var i = 0; i < mutations.length; i++) {
      var nodes = mutations[i].addedNodes || [];
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches("[data-grid-cart]")) hydrateCard(node);
        if (node.querySelectorAll) hydrateAll(node);
      }
    }
  });

  function boot() {
    hydrate();
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
